import { FormEvent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button, LinkButton } from "../../components/Button";
import { Form } from "../../components/Form/styles";
import { Box, Container as BarContainer, StickySpacer } from "../../components/SaveBar/styles";
import { useToast } from "../../components/Toast";
import { useCepLookup } from "../../hooks/CepLookup";
import CheckIcon from "../../icons/Check";
import { api } from "../../services/api";
import { isApiError } from "../../services/api/errors";
import { createRecipient, PaymentsState, RecipientInput } from "../../services/payments";
import { BR_STATES, formatCep, formatDocument, isValidCnpj, isValidCpf, onlyDigits } from "../../utils/brDocument";
import { formatBrazilianPhone } from "../../utils/phone";
import { classNames } from "../../utils/styles";
import { currencySymbol } from "../Agenda/format";
import { Steps, Wrapper } from "../Editor/components/CreationProgress/styles";
import { Card, Section } from "../Settings/styles";
import {
  apiErrors,
  BANK_API_KEYS,
  BankFields,
  BankValues,
  EMPTY_BANK,
  Pair,
  SelectField,
  TextField,
  toBankInput,
  validateBank,
} from "./BankAccountModal";

const prefix = "pages.settings.payments.form";

type Values = {
  name: string;
  document: string;
  /** DD/MM/AAAA, as typed */
  birthdate: string;
  occupation: string;
  /** Reais and cents, masked (3.500,00) */
  monthlyIncome: string;
  phone: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
} & BankValues;

const EMPTY: Values = {
  name: "",
  document: "",
  birthdate: "",
  occupation: "",
  monthlyIncome: "",
  phone: "",
  zipCode: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  ...EMPTY_BANK,
};

/** Each field's key in the API's errors (dotted, as the request body). */
const API_KEYS: Record<keyof Values, string> = {
  name: "name",
  document: "document",
  birthdate: "birthdate",
  occupation: "occupation",
  monthlyIncome: "monthlyIncomeCents",
  phone: "phone",
  zipCode: "address.zipCode",
  street: "address.street",
  number: "address.number",
  complement: "address.complement",
  neighborhood: "address.neighborhood",
  city: "address.city",
  state: "address.state",
  ...BANK_API_KEYS,
};
const FIELD_KEYS = new Set(Object.values(API_KEYS));

/** The form's three steps, one card each: the person, their address, the bank account. */
const STEPS = ["personal", "address", "bank"] as const;
type Step = (typeof STEPS)[number];
const STEP_FIELDS: Record<Step, (keyof Values)[]> = {
  personal: ["name", "document", "birthdate", "phone", "occupation", "monthlyIncome"],
  address: ["zipCode", "street", "number", "complement", "neighborhood", "city", "state"],
  bank: Object.keys(BANK_API_KEYS) as (keyof BankValues)[],
};
const stepOfApiKey = (apiKey: string) =>
  STEPS.findIndex(step => STEP_FIELDS[step].some(key => API_KEYS[key] === apiKey));

// Where to find a CEP from the address, for whoever doesn't know theirs
const CORREIOS_CEP_SEARCH = "https://buscacepinter.correios.com.br/app/endereco/index.php";

const MIN_AGE = 18;

const formatDate = (value: string) =>
  onlyDigits(value)
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, "$1/$2")
    .replace(/(\d{2})(\d)/, "$1/$2");

/**
 * Reais and cents as they're typed, filled from the cents' side (as the agenda's price): "5" → "0,05",
 * "350000" → "3.500,00"; a pasted "R$ 1.234,56" keeps its cents.
 */
const formatReais = (value: string) => {
  const digits = onlyDigits(value)
    .replace(/^0+(?=\d)/, "")
    .slice(0, 11);
  if (!digits) return "";
  const cents = digits.padStart(3, "0");
  return `${cents.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${cents.slice(-2)}`;
};

/** The masked income in cents (always two cents' digits). */
const incomeCents = (value: string) => Number(onlyDigits(value));

/** A saved phone without the country code (+55 48 99999-0000 → 48999990000). */
const nationalPhone = (value: string) => {
  const digits = onlyDigits(value);
  return digits.length > 11 && digits.startsWith("55") ? digits.slice(2) : digits;
};

/** DD/MM/AAAA to YYYY-MM-DD, or undefined when it isn't a real date. */
const isoDate = (value: string) => {
  const digits = onlyDigits(value);
  if (digits.length !== 8) return undefined;
  const [day, month, year] = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)];
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const iso = `${year}-${month}-${day}`;
  return date.toISOString().slice(0, 10) === iso ? iso : undefined;
};

/** A birth date that hasn't happened yet is not a date of birth. */
const isFuture = (iso: string) => {
  const today = new Date();
  const todayIso = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  return iso > todayIso;
};

const isAdult = (iso: string) => {
  const [year, month, day] = iso.split("-").map(Number);
  const today = new Date();
  const adultOn = new Date(year + MIN_AGE, month - 1, day);
  return adultOn <= new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

/**
 * Area code and number (10 or 11 digits), with or without +55. The API takes any number of that shape; here a mobile
 * (11 digits) must start with 9 after the area code, as all of them do: a typo is caught before the provider says so.
 */
const isBrazilianPhone = (value: string) => {
  let digits = onlyDigits(value);
  if (digits.length > 11 && digits.startsWith("55")) digits = digits.slice(2);
  return /^[1-9][1-9](\d{8}|9\d{8})$/.test(digits);
};

/** What's wrong with each field (by its API key), as translation keys under `form.errors`. */
const validate = (values: Values): Record<string, string> => {
  const errors: Partial<Record<keyof Values, string>> = { ...validateBank(values) };
  const document = onlyDigits(values.document);
  if (!values.name.trim()) errors.name = "name";
  if (isValidCnpj(document)) errors.document = "cnpj";
  else if (!isValidCpf(document)) errors.document = "document";
  const birthdate = isoDate(values.birthdate);
  if (!birthdate || isFuture(birthdate)) errors.birthdate = "birthdate";
  else if (!isAdult(birthdate)) errors.birthdate = "underage";
  if (!values.occupation.trim()) errors.occupation = "occupation";
  // the API takes at least R$ 1,00 (Pagar.me gets whole reais)
  if (incomeCents(values.monthlyIncome) < 100) errors.monthlyIncome = "monthlyIncome";
  if (!isBrazilianPhone(values.phone)) errors.phone = "phone";
  if (onlyDigits(values.zipCode).length !== 8) errors.zipCode = "zipCode";
  (["street", "number", "neighborhood", "city"] as const).forEach(key => {
    if (!values[key].trim()) errors[key] = "required";
  });
  if (!BR_STATES.includes(values.state)) errors.state = "state";

  return Object.fromEntries(Object.entries(errors).map(([key, error]) => [API_KEYS[key as keyof Values], error]));
};

const toInput = (values: Values): RecipientInput => ({
  document: onlyDigits(values.document),
  name: values.name.trim(),
  birthdate: isoDate(values.birthdate) as string,
  phone: onlyDigits(values.phone),
  occupation: values.occupation.trim(),
  monthlyIncomeCents: incomeCents(values.monthlyIncome),
  address: {
    zipCode: onlyDigits(values.zipCode),
    street: values.street.trim(),
    number: values.number.trim(),
    complement: values.complement.trim(),
    neighborhood: values.neighborhood.trim(),
    city: values.city.trim(),
    state: values.state,
  },
  bank: toBankInput(values),
});

type Props = {
  siteId: string;
  /** Sent: the screen's new state (the registration under review). */
  onDone: (state: PaymentsState) => void;
  /** The Keepo already has its registration (sent elsewhere meanwhile): the screen reloads and shows it. */
  onAlreadyLinked: () => void;
  onCancel: () => void;
  /** The screen's header (back link and title), under the steps, as on the screens of creating a Keepo. */
  header?: ReactNode;
};

/**
 * The registration to receive payments (Pagar.me recipient), individuals (CPF) only for now: the person, their
 * address and the bank account in the same CPF. Starts with the billing details and phone the account has saved.
 */
export const RecipientForm = ({ siteId, onDone, onAlreadyLinked, onCancel, header }: Props) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Values>(EMPTY);
  // What's wrong with each field, by the API's dotted keys (ours translated, or the server's own texts)
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setSubmitting] = useState(false);
  // One registration at a time: set at once (the state above only changes on the next render, and a second Enter can
  // come before it), so a repeated Enter or "Ir" on the phone's keyboard never sends it twice
  const sending = useRef(false);
  const toast = useToast();
  const topRef = useRef<HTMLDivElement>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  // A full CEP fills the rest of the address from ViaCEP; what comes back lands on the latest values
  const cep = useCepLookup();
  const [isAddressOpened, setAddressOpen] = useState(false);
  const latest = useRef(values);
  latest.current = values;
  // Open for good once looked up, or when the account already had an address
  const isAddressOpen =
    isAddressOpened || !!(values.street || values.number || values.neighborhood || values.city || values.state);

  const formRef = useRef<HTMLFormElement>(null);
  // Moved to another step, or something's wrong: the cursor goes to the first wrong field, else to the step's first
  // field (screen readers follow). Not on the first step's opening.
  const moved = useRef(false);
  const [focusTick, setFocusTick] = useState(0);
  const focusFirst = () => {
    moved.current = true;
    setFocusTick(tick => tick + 1);
  };
  const goTo = (index: number) => {
    moved.current = true;
    setStepIndex(index);
    topRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    const form = formRef.current;
    (
      form?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      form?.querySelector<HTMLElement>("input, select, button:not([hidden])")
    )?.focus();
  }, [stepIndex, focusTick]);

  useEffect(() => {
    let active = true;
    api
      .getUser()
      .then(user => {
        if (!active) return;
        const billing = user.billing || {};
        const saved: Partial<Values> = {
          name: billing.name || user.name || "",
          document: formatDocument(billing.document || ""),
          phone: formatBrazilianPhone(nationalPhone(user.phone || "")),
          zipCode: formatCep(billing.postalCode || ""),
          street: billing.line1 || "",
          number: billing.number || "",
          complement: billing.line2 || "",
          neighborhood: billing.district || "",
          city: billing.city || "",
          state: BR_STATES.includes(billing.state || "") ? billing.state : "",
        };
        // Only what's still empty: the person may have started typing
        setValues(current => {
          const next = { ...current };
          (Object.keys(saved) as (keyof Values)[]).forEach(key => {
            if (!current[key] && saved[key]) (next as Record<string, string>)[key] = saved[key] as string;
          });
          return next;
        });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const set = (key: keyof Values, value: string) => {
    setValues(current => ({ ...current, [key]: value }));
    // The field changed: its error no longer applies
    setErrors(current => {
      const next = { ...current };
      delete next[API_KEYS[key]];
      return next;
    });
  };

  const errorOf = (key: keyof Values) => errors[API_KEYS[key]];

  const changeCep = (typed: string) => {
    const zipCode = formatCep(typed);
    set("zipCode", zipCode);
    cep.search(zipCode, {
      onFound: address => {
        setAddressOpen(true);
        const current = latest.current;
        setValues({
          ...current,
          street: address.street || current.street,
          neighborhood: address.neighborhood || current.neighborhood,
          city: address.city || current.city,
          state: BR_STATES.includes(address.state) ? address.state : current.state,
        });
        setErrors(current => {
          const next = { ...current };
          (["street", "neighborhood", "city", "state"] as const).forEach(key => delete next[API_KEYS[key]]);
          return next;
        });
        // Once the fields are on screen, the number is next: unless the person already went to another field
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (!active || active === document.body || active.id === "recipient-zipCode") {
            document.getElementById("recipient-number")?.focus();
          }
        });
      },
      // Not found, or ViaCEP down: the address is typed by hand. What the CEP before had filled isn't this CEP's:
      // cleared, so a street and city of another place are never sent with it
      onMiss: () => {
        setAddressOpen(true);
        setValues({ ...latest.current, street: "", neighborhood: "", city: "", state: "" });
      },
    });
  };

  /** Our errors as texts, by API key. */
  const translated = (found: [string, string][]) =>
    Object.fromEntries(found.map(([key, error]) => [key, t(`${prefix}.errors.${error}`)]));

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (sending.current) return;
    const all = Object.entries(validate(values));

    // Before the last step, only this step's fields: the next ones haven't been filled yet
    if (!isLast) {
      const keys = new Set(STEP_FIELDS[step].map(key => API_KEYS[key]));
      const found = all.filter(([key]) => keys.has(key));
      setErrors(translated(found));
      // Errors on the address fields still hidden (CEP not looked up): show them
      if (step === "address" && found.some(([key]) => key !== API_KEYS.zipCode)) setAddressOpen(true);
      if (found.length === 0) {
        goTo(stepIndex + 1);
      } else {
        focusFirst();
        toast.error(t(`${prefix}.errors.check`));
      }
      return;
    }

    setErrors(translated(all));
    if (all.length > 0) {
      // Something wrong in an earlier step (e.g. erased meanwhile): back to it
      const first = Math.min(...all.map(([key]) => stepOfApiKey(key)).filter(index => index >= 0));
      if (Number.isFinite(first) && first !== stepIndex) goTo(first);
      focusFirst();
      toast.error(t(`${prefix}.errors.check`));
      return;
    }

    sending.current = true;
    setSubmitting(true);
    try {
      onDone(await createRecipient(siteId, toInput(values)));
    } catch (error) {
      sending.current = false;
      setSubmitting(false);
      // Registered meanwhile (another tab or device): nothing to fix here, the screen shows what is there
      if (isApiError(error, "RECIPIENT_ALREADY_LINKED")) {
        onAlreadyLinked();
        return;
      }
      if (isApiError(error, "PROVIDER_UNAVAILABLE")) {
        toast.error(t(`${prefix}.errors.unavailable`));
        return;
      }
      const { onFields, others } = apiErrors(error, FIELD_KEYS, t);
      setErrors(onFields);
      // The provider refused a field of an earlier step: that step, with the error on the field
      const steps = Object.keys(onFields)
        .map(stepOfApiKey)
        .filter(index => index >= 0);
      if (steps.length > 0) {
        const first = Math.min(...steps);
        if (STEPS[first] === "address") setAddressOpen(true);
        if (first !== stepIndex) goTo(first);
      }
      if (Object.keys(onFields).length > 0) focusFirst();
      if (others.length > 0) toast.error(others.join(" "));
      else if (Object.keys(onFields).length === 0) toast.error(t(`${prefix}.errors.generic`));
      else toast.error(t(`${prefix}.errors.check`));
    }
  };

  const text = (
    key: keyof Values,
    options: { format?: (value: string) => string; type?: string; max?: number } = {}
  ) => (
    <TextField
      id={`recipient-${key}`}
      label={t(`${prefix}.${key}`)}
      value={values[key]}
      error={errorOf(key)}
      format={options.format}
      type={options.type}
      max={options.max}
      placeholder={key === "birthdate" ? t(`${prefix}.birthdatePlaceholder`) : undefined}
      prefix={key === "monthlyIncome" ? currencySymbol("BRL") : undefined}
      onChange={value => set(key, value)}
    />
  );

  const select = (key: keyof Values, label: string, options: { value: string; label: string }[]) => (
    <SelectField
      id={`recipient-${key}`}
      label={label}
      value={values[key]}
      error={errorOf(key)}
      options={options}
      onChange={value => set(key, value)}
    />
  );

  // The bar's buttons aren't <button>s: Enter and Space do what a click does
  const onKeys = (action: () => void) => (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };
  const cancel = () => !isSubmitting && onCancel();
  const back = () => !isSubmitting && goTo(stepIndex - 1);
  // Always does something: with the step incomplete it says what is wrong (a button that does nothing says nothing)
  const advance = () => submit();

  return (
    <>
      {/* where the person is, on top (the same steps as creating a Keepo); the screen's own header under it */}
      <Wrapper ref={topRef}>
        <Steps aria-label={t(`${prefix}.stepOf`, { current: stepIndex + 1, total: STEPS.length })}>
          {STEPS.map((item, index) => (
            <li
              key={item}
              className={classNames({ current: index === stepIndex, done: index < stepIndex })}
              aria-current={index === stepIndex ? "step" : undefined}>
              <span className="dot">
                {index < stepIndex ? <CheckIcon width={10} height={10} color="currentColor" /> : index + 1}
              </span>
              <span className="label">{t(`${prefix}.${item}`)}</span>
            </li>
          ))}
        </Steps>
      </Wrapper>
      {header}
      <Form ref={formRef} onSubmit={submit} noValidate>
        {/* Enter in a field moves on, as the bar's button */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />

        {step === "personal" && (
          <Section>
            <Card>
              {text("name", { max: 128 })}
              <div>
                {text("document", { format: formatDocument, max: 18 })}
                {!errorOf("document") && <Note>{t(`${prefix}.onlyIndividuals`)}</Note>}
              </div>
              <Pair>
                {text("birthdate", { format: formatDate, max: 10 })}
                {text("phone", { format: formatBrazilianPhone, type: "tel", max: 15 })}
              </Pair>
              <Pair>
                {text("occupation", { max: 100 })}
                {text("monthlyIncome", { format: formatReais })}
              </Pair>
            </Card>
          </Section>
        )}

        {step === "address" && (
          <Section>
            <Card>
              <TextField
                id="recipient-zipCode"
                label={t(`${prefix}.zipCode`)}
                value={values.zipCode}
                error={
                  cep.status === "notFound"
                    ? t(`${prefix}.errors.cepNotFound`)
                    : cep.status === "unavailable"
                      ? t(`${prefix}.errors.cepUnavailable`)
                      : errorOf("zipCode")
                }
                max={9}
                placeholder="00000-000"
                onChange={changeCep}
                suffix={
                  // inside the field, on the right, until something is typed (as the checkout's)
                  !values.zipCode && (
                    <CepLink href={CORREIOS_CEP_SEARCH} target="_blank" rel="noreferrer">
                      {t(`${prefix}.cepUnknown`)}
                    </CepLink>
                  )
                }
              />
              {cep.status === "loading" && <Note role="status">{t(`${prefix}.cepLoading`)}</Note>}
              {isAddressOpen && (
                <>
                  {text("street", { max: 255 })}
                  <Pair>
                    {text("number", { max: 20 })}
                    {text("complement", { max: 100 })}
                  </Pair>
                  {text("neighborhood", { max: 100 })}
                  <Pair>
                    {text("city", { max: 100 })}
                    {select("state", t(`${prefix}.state`), [
                      { value: "", label: t(`${prefix}.choose`) },
                      ...BR_STATES.map(uf => ({ value: uf, label: uf })),
                    ])}
                  </Pair>
                </>
              )}
            </Card>
          </Section>
        )}

        {step === "bank" && (
          <Section>
            <Card>
              <BankFields idPrefix="recipient" values={values} errors={errors} onChange={set} />
            </Card>
          </Section>
        )}
      </Form>

      {/* Same fixed bar as creating a Keepo */}
      <StickySpacer />
      <Box $sticky>
        <BarContainer>
          {stepIndex === 0 ? (
            <LinkButton role="button" tabIndex={0} onClick={cancel} onKeyDown={onKeys(cancel)}>
              {t(`${prefix}.cancel`)}
            </LinkButton>
          ) : (
            <LinkButton role="button" tabIndex={0} onClick={back} onKeyDown={onKeys(back)}>
              {t(`${prefix}.back`)}
            </LinkButton>
          )}
          <Button
            role="button"
            tabIndex={0}
            type="INFO"
            size="LG"
            disabled={isSubmitting}
            aria-disabled={isSubmitting}
            $loading={isSubmitting}
            onClick={advance}
            onKeyDown={onKeys(advance)}>
            {t(`${prefix}.${isLast ? "submit" : "next"}`)}
          </Button>
        </BarContainer>
      </Box>
    </>
  );
};

const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
`;

/* "Não sei meu CEP", inside the CEP field on the right */
const CepLink = styled.a`
  font-family: Inter, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-neutral-150);
  white-space: nowrap;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--color-neutral-white);
  }
`;
